import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import axios from "axios";
import dayjs from "dayjs";
import BgBox from "../../common/BgBox";
import CardUI from "../../common/Card";
import HeaderText from "../../common/HeaderText";
import AlertMessage from "../../common/AlertMessage";
import MccLogo from "../../components/MccLogo";
import { BASE_URL } from "../../actions";
import { clearContact, formatPhoneNumber } from "../../utils/helpers";

const PHONE_REGEX = /^\+\d{1} \(\d{3}\) \d{3}-\d{4}$/;
const OTP_LENGTH = 6;
const numbers = /^[0-9\b]+$/;

let currentOtpIndex = 0;

const STEPS = {
  MOBILE: "mobile",
  OTP: "otp",
  VERIFIED: "verified",
  DELETED: "deleted",
};

const DetailRow = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="flex-start"
    gap="16px"
  >
    <Typography variant="body2" color="#566875" fontSize={14} minWidth="110px">
      {label}
    </Typography>
    <Typography
      variant="body1"
      color="#212121"
      fontSize={15}
      fontWeight={500}
      textAlign="right"
      sx={{ wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const RequestAccountDeletion = () => {
  const [step, setStep] = useState(STEPS.MOBILE);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(""));
  const [activeOtpIndex, setActiveOtpIndex] = useState(0);
  const [patient, setPatient] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const inputRef = useRef();
  const canRequestDeletion = deleteConfirmText.trim().toLowerCase() === "delete";

  useEffect(() => {
    if (step === STEPS.OTP) {
      inputRef.current?.focus();
    }
  }, [activeOtpIndex, step]);

  const showAlert = (message, severity = "error") => {
    setAlertMsg(message);
    setAlertSeverity(severity);
    setOpenAlert(true);
  };

  const handleCloseAlert = (_event, reason) => {
    if (reason === "clickaway") return;
    setOpenAlert(false);
  };

  const contactNumberHandler = (e) => {
    const num = clearContact(e.target.value);
    setMobile(formatPhoneNumber(num));
  };

  const isPhoneValid = PHONE_REGEX.test(mobile);
  const isOtpComplete = otp.every((digit) => digit !== "");

  const sendOtpHandler = async (e) => {
    e.preventDefault();
    if (!isPhoneValid) {
      showAlert("Please enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${BASE_URL}/patient/send-otp`, {
        contact_number: mobile,
      });

      if (data?.ok) {
        setOtp(new Array(OTP_LENGTH).fill(""));
        setActiveOtpIndex(0);
        setStep(STEPS.OTP);
        showAlert("OTP sent to your mobile number", "success");
      } else {
        showAlert(data?.message || data?.msg || "Unable to send OTP");
      }
    } catch (error) {
      showAlert(
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          "Unable to send OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpHandler = async (e) => {
    e.preventDefault();
    const otpNum = otp.join("");
    if (otpNum.length !== OTP_LENGTH) {
      showAlert("Please enter the 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${BASE_URL}/patient/verify-otp`, {
        contact_number: mobile,
        otp: otpNum,
      });

      if (data?.ok) {
        setPatient(data?.data || null);
        setAuthToken(data?.token || null);
        setStep(STEPS.VERIFIED);
        showAlert("Mobile number verified successfully", "success");
      } else {
        showAlert(data?.message || data?.msg || "Invalid OTP");
      }
    } catch (error) {
      showAlert(
        error?.response?.data?.message ||
          error?.response?.data?.msg ||
          "Invalid OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const otpChangeHandler = (e) => {
    const { value } = e.target;
    const newOtp = [...otp];

    if (value === "" || numbers.test(value)) {
      newOtp[currentOtpIndex] = value.substring(value.length - 1);
      if (!value) {
        setActiveOtpIndex(currentOtpIndex - 1);
      } else {
        setActiveOtpIndex(currentOtpIndex + 1);
      }
    }
    setOtp(newOtp);
  };

  const keyDownHandler = (e, index) => {
    currentOtpIndex = index;
    if (e.key === "Backspace") {
      setActiveOtpIndex(currentOtpIndex - 1);
    }
  };

  const pasteOtpHandler = (e) => {
    e.preventDefault();
    const pasteText = (e.clipboardData || window.clipboardData)
      .getData("text/plain")
      .trim()
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH)
      .split("");

    if (pasteText.length === OTP_LENGTH) {
      setOtp(pasteText);
      setActiveOtpIndex(OTP_LENGTH - 1);
    }
  };

  const changeNumberHandler = () => {
    setStep(STEPS.MOBILE);
    setOtp(new Array(OTP_LENGTH).fill(""));
    setPatient(null);
    setAuthToken(null);
    setDeleteConfirmText("");
  };

  const submitDeletionRequest = async () => {
    setDeleteLoading(true);
    try {
      const { data } = await axios.post(
        `${BASE_URL}/patient/delete`,
        { patient_id: patient?.id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (!data?.ok) {
        throw new Error(data?.msg || data?.message);
      }

      setConfirmOpen(false);
      setDeleteConfirmText("");
      setStep(STEPS.DELETED);
      showAlert("Account deletion request submitted", "success");
    } catch (error) {
      showAlert(
        error?.response?.data?.msg ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to submit deletion request. Please try again."
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const fullName = [patient?.first_name, patient?.last_name]
    .filter(Boolean)
    .join(" ");

  const formattedDob = patient?.dob
    ? dayjs(patient.dob).format("MMM D, YYYY")
    : null;

  const renderHeader = () => {
    if (step === STEPS.DELETED) {
      return (
        <Box textAlign="center">
          <CheckCircleRoundedIcon
            sx={{ fontSize: 48, color: "#2E7D32", mb: 1 }}
          />
          <HeaderText
            variant="h3"
            fontSize={28}
            textContent="Deleted Successfully"
            lineHeight="32px"
            textAlign="center"
          />
          <Typography
            variant="body1"
            fontSize={16}
            mt="12px"
            color="#566875"
          >
            Your account has been successfully deleted.
          </Typography>
        </Box>
      );
    }

    if (step === STEPS.VERIFIED) {
      return (
        <Box textAlign="center">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap="8px"
            mb="12px"
          >
            <CheckCircleRoundedIcon sx={{ color: "#2E7D32", fontSize: 28 }} />
            <Typography
              variant="body1"
              fontWeight={600}
              fontSize={16}
              color="#2E7D32"
            >
              Verified
            </Typography>
          </Stack>
          <HeaderText
            variant="h3"
            fontSize={28}
            textContent="Your account details"
            lineHeight="32px"
            textAlign="center"
          />
          <Typography
            variant="body1"
            fontSize={16}
            mt="12px"
            color="#566875"
          >
            Review your details below. You can request permanent deletion of
            your account.
          </Typography>
        </Box>
      );
    }

    if (step === STEPS.OTP) {
      return (
        <Box textAlign="center">
          <HeaderText
            variant="h3"
            fontSize={28}
            textContent="Enter the 6 digit code"
            lineHeight="32px"
            textAlign="center"
          />
          <Typography
            variant="body1"
            fontSize={16}
            mt="12px"
            color="#566875"
          >
            We&apos;ve sent a six digit code to {mobile}
          </Typography>
        </Box>
      );
    }

    return (
      <Box textAlign="center">
        <HeaderText
          variant="h3"
          fontSize={28}
          textContent="Request account deletion"
          lineHeight="32px"
          textAlign="center"
        />
        <Typography variant="body1" fontSize={16} mt="12px" color="#566875">
          Enter your registered mobile number to verify your identity and
          request account deletion.
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {alertMsg && (
        <AlertMessage
          message={alertMsg}
          severity={alertSeverity}
          open={openAlert}
          handleClose={handleCloseAlert}
        />
      )}

      <BgBox>
        <Box
          width="100%"
          maxWidth={520}
          pt="56px"
          sx={{ overflow: "visible" }}
        >
          <CardUI paddingTop="86px" maxWidth={520} overflow="visible">
            <MccLogo />

            <Grid container component="main">
              <Box
                width="100%"
                display="flex"
                flexDirection="column"
                gap="32px"
              >
                {renderHeader()}

              {step === STEPS.MOBILE && (
                <Box
                  component="form"
                  onSubmit={sendOtpHandler}
                  display="flex"
                  flexDirection="column"
                  gap="16px"
                >
                  <TextField
                    value={mobile}
                    onChange={contactNumberHandler}
                    placeholder="+1 (555) 123-4567"
                    label="Mobile number"
                    variant="outlined"
                    name="mobile"
                    required
                    fullWidth
                    inputProps={{ inputMode: "tel", maxLength: 17 }}
                  />
                  <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    loading={loading}
                    disabled={!isPhoneValid}
                    sx={{ padding: "8px 22px", fontSize: 15 }}
                  >
                    Send OTP
                  </LoadingButton>
                </Box>
              )}

              {step === STEPS.OTP && (
                <Box
                  component="form"
                  onSubmit={verifyOtpHandler}
                  display="flex"
                  flexDirection="column"
                  gap="24px"
                >
                  <Box
                    width="100%"
                    display="flex"
                    alignItems="center"
                    gap="12px"
                  >
                    {otp.map((digit, index) => (
                      <TextField
                        key={index}
                        inputRef={index === activeOtpIndex ? inputRef : null}
                        variant="outlined"
                        type="text"
                        value={digit}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        onChange={otpChangeHandler}
                        onKeyDown={(e) => keyDownHandler(e, index)}
                        onFocus={() => {
                          currentOtpIndex = index;
                          setActiveOtpIndex(index);
                        }}
                        onPaste={pasteOtpHandler}
                        sx={{
                          "& input": {
                            textAlign: "center",
                            fontSize: "22px",
                            padding: "12px",
                          },
                        }}
                        inputProps={{ maxLength: 1 }}
                      />
                    ))}
                  </Box>

                  <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    loading={loading}
                    disabled={!isOtpComplete}
                    sx={{ padding: "8px 22px", fontSize: 15 }}
                  >
                    Verify OTP
                  </LoadingButton>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap="8px"
                  >
                    <Button
                      variant="text"
                      onClick={changeNumberHandler}
                      sx={{ textTransform: "none", fontSize: 14 }}
                    >
                      Change number
                    </Button>
                    <LoadingButton
                      variant="text"
                      loading={loading}
                      onClick={sendOtpHandler}
                      sx={{ textTransform: "none", fontSize: 14 }}
                    >
                      Resend OTP
                    </LoadingButton>
                  </Stack>
                </Box>
              )}

              {step === STEPS.VERIFIED && (
                <Box display="flex" flexDirection="column" gap="24px">
                  <Box
                    border="1px solid #E3E8EE"
                    borderRadius="8px"
                    padding="20px"
                    display="flex"
                    flexDirection="column"
                    gap="14px"
                    bgcolor="#FAFBFC"
                  >
                    <DetailRow label="Name" value={fullName} />
                    <Divider />
                    <DetailRow
                      label="Mobile"
                      value={patient?.contact_number || mobile}
                    />
                    <Divider />
                    <DetailRow label="Email" value={patient?.email} />
                    {formattedDob && (
                      <>
                        <Divider />
                        <DetailRow label="Date of birth" value={formattedDob} />
                      </>
                    )}
                    {patient?.mrn && (
                      <>
                        <Divider />
                        <DetailRow label="MRN" value={patient.mrn} />
                      </>
                    )}
                  </Box>

                  <Box
                    border="1px solid #F5C2C7"
                    borderRadius="8px"
                    padding="20px"
                    bgcolor="#FFF8F8"
                    display="flex"
                    flexDirection="column"
                    gap="16px"
                  >
                    <Stack direction="row" alignItems="flex-start" gap="10px">
                      <WarningAmberRoundedIcon
                        sx={{ color: "#D32F2F", mt: "2px" }}
                      />
                      <Box>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          fontSize={15}
                          color="#212121"
                        >
                          Request account deletion
                        </Typography>
                        <Typography
                          variant="body2"
                          fontSize={14}
                          color="#566875"
                          mt="4px"
                        >
                          This will permanently remove your account and
                          associated data. This action cannot be undone.
                        </Typography>
                      </Box>
                    </Stack>

                    <TextField
                      fullWidth
                      label='Type "delete" to confirm'
                      placeholder="delete"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      variant="outlined"
                      autoComplete="off"
                      helperText='Enter the word "delete" to enable the button below'
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      disabled={!canRequestDeletion}
                      onClick={() => setConfirmOpen(true)}
                      sx={{ padding: "8px 22px", fontSize: 15 }}
                    >
                      Request account deletion
                    </Button>
                  </Box>
                </Box>
              )}

              {step === STEPS.DELETED && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setStep(STEPS.MOBILE);
                    setMobile("");
                    setOtp(new Array(OTP_LENGTH).fill(""));
                    setPatient(null);
                    setAuthToken(null);
                    setDeleteConfirmText("");
                  }}
                  sx={{ padding: "8px 22px", fontSize: 15 }}
                >
                  Done
                </Button>
              )}
              </Box>
            </Grid>
          </CardUI>
        </Box>
      </BgBox>

      <Dialog
        open={confirmOpen}
        onClose={() => !deleteLoading && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 20 }}>
          Confirm account deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 15, color: "#566875" }}>
            Are you sure you want to request deletion of your account
            {fullName ? ` for ${fullName}` : ""}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "8px 24px 20px", gap: "8px" }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={deleteLoading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={deleteLoading}
            onClick={submitDeletionRequest}
            sx={{ textTransform: "none" }}
          >
            Yes, request deletion
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RequestAccountDeletion;
